import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import Wordmark from "./Wordmark";
import { api } from "../services";

// Completion payoff. Direct numbers, no fabricated psychology ("you know
// them better than they know themselves" etc.) — the number creates the
// conversation. "MAKE THEM GUESS YOURS" is the reciprocal-loop hypothesis:
// a small, self-contained share affordance (copy link / Web Share API),
// reusing the same pattern CompareFriendModal already uses — no new
// share-pack engine for this one link.
export default function GuessMyTasteResult({ correctCount, totalRounds, guessSessionId, targetHandle, onDone }) {
  const [reciprocalUrl, setReciprocalUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const [unavailable, setUnavailable] = useState(false);

  // Impression, not click — the reciprocal RATE KPI needs the denominator
  // (how many finishers were actually offered it), so this must fire on
  // display. The click is separately captured by _share_started below.
  useEffect(() => {
    api.logEvent("guess_my_taste_reciprocal_cta_shown", null, { guessSessionId });
  }, [guessSessionId]);

  const makeThemGuess = async () => {
    if (reciprocalUrl || busy) return;
    setBusy(true);
    try {
      const r = await api.createGuessReciprocal(guessSessionId);
      const url = `${window.location.origin}/?guess=${r.guess_session_id}`;
      setReciprocalUrl(url);
      api.logEvent("guess_my_taste_share_started", null, { guessSessionId: r.guess_session_id });
      if (navigator.share) {
        try {
          await navigator.share({ title: "TASTE TEST", text: "Think you know my taste?", url });
          api.logEvent("guess_my_taste_share_completed", null, { guessSessionId: r.guess_session_id });
          return;
        } catch { /* dismissed — fall through to copy */ }
      }
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Link copied", { description: "Send it — they play your taste directly." });
        api.logEvent("guess_my_taste_share_completed", null, { guessSessionId: r.guess_session_id });
      } catch {
        toast.message("Guess link", { description: url });
      }
    } catch {
      // Fails gracefully rather than a dead CTA — most commonly: the
      // original run doesn't have enough resolved rounds to seed a reverse
      // game yet.
      setUnavailable(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center h-[100dvh] gap-6 px-8 text-center max-w-[440px] mx-auto">
      <Wordmark variant="horizontal" className="h-4" />
      <div className="h-px w-24" style={{ background: "linear-gradient(90deg,var(--tt-left),var(--tt-right))" }} />

      <div>
        <p className="font-display font-black text-bone text-6xl tracking-[-0.03em]">
          {correctCount} / {totalRounds}
        </p>
        <p className="mt-2 font-mono text-[11px] tracking-[0.3em] uppercase text-silver">
          Guessed right
        </p>
      </div>

      <p className="font-body text-sm text-silver max-w-xs">
        {targetHandle}'s turn — think they know your taste as well?
      </p>

      {!unavailable ? (
        <button
          type="button"
          data-testid="make-them-guess"
          onClick={makeThemGuess}
          disabled={busy}
          className="w-full rounded-full py-3.5 font-display font-black uppercase tracking-[0.06em] text-sm text-[#05070a] disabled:opacity-60"
          style={{ background: "linear-gradient(90deg,var(--tt-left),var(--tt-right))" }}
        >
          {busy ? "Preparing…" : reciprocalUrl ? "Link copied — send it" : "Make them guess yours"}
        </button>
      ) : (
        <p className="font-body text-[12px] text-white/45">
          Not enough evidence yet to reverse this one — play another Test first.
        </p>
      )}

      <button
        type="button"
        data-testid="guess-result-done"
        onClick={onDone}
        className="font-body text-[11px] tracking-[0.2em] uppercase text-white/45 hover:text-white/75"
      >
        Done
      </button>
    </div>
  );
}
